/**
 * Closed swatch table used to migrate pre-v6 token colours and to stamp defaults.
 * Authored documents store `Rgba`; this module is the only place a leftover token becomes RGB.
 */

import {
  clampRgba,
  isRgba,
  parseHex,
  rgb24,
  rgbaFromRgb24,
  strokeFromFill,
  type Rgba,
} from '../color.ts';

export type PaletteGroup = 'land' | 'rock' | 'water';

export type PaletteSwatch = {
  id: string;
  label: string;
  group: PaletteGroup;
  fill: number;
  stroke: number;
};

export type ResolvedColor = {
  fill: number;
  stroke: number;
  alpha: number;
};

export const PALETTE: readonly PaletteSwatch[] = [
  { id: 'moss', label: 'Moss', group: 'land', fill: 0x3d6b4f, stroke: 0x2a4a36 },
  { id: 'pine', label: 'Pine', group: 'land', fill: 0x2f5540, stroke: 0x1d3728 },
  { id: 'heath', label: 'Heath', group: 'land', fill: 0x6b6a43, stroke: 0x47462c },
  { id: 'dune', label: 'Dune', group: 'land', fill: 0xa9986a, stroke: 0x7c6e49 },
  { id: 'slate', label: 'Slate', group: 'land', fill: 0x5d6570, stroke: 0x3d434b },
  { id: 'basalt', label: 'Basalt', group: 'land', fill: 0x3a3d42, stroke: 0x24262a },
  { id: 'chalk', label: 'Chalk', group: 'land', fill: 0xc9c6b4, stroke: 0x94917f },

  { id: 'granite', label: 'Granite', group: 'rock', fill: 0x8b8f96, stroke: 0x565a61 },
  { id: 'basalt-rock', label: 'Basalt', group: 'rock', fill: 0x4a4d52, stroke: 0x2b2d31 },
  { id: 'sandstone', label: 'Sandstone', group: 'rock', fill: 0xb59a72, stroke: 0x846f4d },
  { id: 'mossy-rock', label: 'Mossy', group: 'rock', fill: 0x6f7b5e, stroke: 0x49523c },
  { id: 'reef-rock', label: 'Reef', group: 'rock', fill: 0x4c6f74, stroke: 0x2c474b },

  { id: 'shallow-sand', label: 'Sand bar', group: 'water', fill: 0xd9c48a, stroke: 0xb0995f },
  { id: 'shallow-stone', label: 'Stone shelf', group: 'water', fill: 0x8a94a0, stroke: 0x5c6570 },
  { id: 'reef', label: 'Reef', group: 'water', fill: 0x5f8f86, stroke: 0x2f5f57 },
  { id: 'lagoon', label: 'Lagoon', group: 'water', fill: 0x6fc3c9, stroke: 0x3d8c92 },
  { id: 'silt', label: 'Silt', group: 'water', fill: 0x7d7a63, stroke: 0x555342 },
  { id: 'start-teal', label: 'Start teal', group: 'water', fill: 0x2b6f77, stroke: 0x8fd8df },
  { id: 'park-gold', label: 'Park gold', group: 'water', fill: 0xc4a35a, stroke: 0xf0d48a },
];

const FALLBACK: PaletteSwatch = {
  id: 'moss',
  label: 'Moss',
  group: 'land',
  fill: 0x3d6b4f,
  stroke: 0x2a4a36,
};

export function rgbaFromToken(id: string): Rgba {
  const swatch = PALETTE.find((entry) => entry.id === id) ?? FALLBACK;

  return rgbaFromRgb24(swatch.fill, 1);
}

export const DEFAULT_LAND_COLOR = rgbaFromToken('moss');
export const DEFAULT_ROCK_COLOR = rgbaFromToken('granite');
export const DEFAULT_REEF_COLOR = rgbaFromToken('reef-rock');
export const DEFAULT_WATER_COLOR = rgbaFromToken('shallow-sand');
export const DEFAULT_START_AREA_COLOR = rgbaFromToken('start-teal');
export const DEFAULT_PARK_AREA_COLOR = rgbaFromToken('park-gold');

export function paletteFor(group: PaletteGroup): readonly PaletteSwatch[] {
  return PALETTE.filter((swatch) => swatch.group === group);
}

/** Unknown tokens resolve to a visible colour rather than throwing, so a hand-edited map still loads. */
export function resolveSwatch(id: string): PaletteSwatch {
  return PALETTE.find((swatch) => swatch.id === id) ?? FALLBACK;
}

export function isPaletteToken(id: string): boolean {
  return PALETTE.some((swatch) => swatch.id === id);
}

/**
 * Turns a persisted colour (RGBA, leftover token, or hex) into fill / derived stroke / alpha.
 */
export function resolveColor(color: unknown): ResolvedColor {
  const rgba = colorFromUnknown(color, DEFAULT_LAND_COLOR);
  const fill = rgb24(rgba);

  if (typeof color === 'string' && isPaletteToken(color)) {
    const swatch = resolveSwatch(color);

    return { fill: swatch.fill, stroke: swatch.stroke, alpha: 1 };
  }

  return { fill, stroke: strokeFromFill(fill), alpha: rgba.a };
}

/** Normalise anything migrate or a setter might see into a current `Rgba`. */
export function colorFromUnknown(value: unknown, fallback: Rgba): Rgba {
  if (isRgba(value)) {
    return clampRgba(value);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return rgbaFromRgb24(value, 1);
  }

  if (typeof value === 'string' && value.length > 0) {
    const hex = parseHex(value, 1);

    if (hex) {
      return hex;
    }

    if (isPaletteToken(value)) {
      return rgbaFromToken(value);
    }
  }

  return clampRgba(fallback);
}
